import { useCallback, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { CampaignDraftAPI } from "src/services/CampaignDraftAPI";
import { WhatsAppCampaignAPI } from "src/services/WhatsAppCampaignAPI";
import { getThrownApiDetail, showApiDetailToast } from "src/utils/showApiDetailToast";
import { useAudienceListsAndCompany } from "../../../CampaignSetup/UseAudienceListsAndCompany";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppAiFilledField,
  WhatsAppButtonDraft,
  WhatsAppButtonType,
  WhatsAppTemplate,
  WhatsAppTemplateFormValues,
} from "../WhatsAppCampaignTypes";
import {
  applyGeneratedFields,
  createButtonDraft,
  createInitialFormValues,
  hasFormErrors,
  templateToFormValues,
  toDraftData,
  toCreatePayload,
  validateTemplateForm,
} from "../WhatsAppTemplateFormUtils";
import { useWhatsAppAudience } from "./UseWhatsAppAudience";
import { useWhatsAppMediaUpload } from "./UseWhatsAppMediaUpload";

const FORM_KEY = `${WHATSAPP_EDITOR_KEY}.form`;

interface UseWhatsAppTemplateFormParams {
  initialTemplate: WhatsAppTemplate | null;
  initialSendTo: string[];
  useAi: boolean;
  onDraftSaved: () => void;
}

export const useWhatsAppTemplateForm = ({
  initialTemplate,
  initialSendTo,
  useAi,
  onDraftSaved,
}: UseWhatsAppTemplateFormParams) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<WhatsAppTemplateFormValues>(() =>
    initialTemplate
      ? templateToFormValues(initialTemplate, initialSendTo)
      : createInitialFormValues(initialSendTo)
  );
  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submittedName, setSubmittedName] = useState<string | null>(null);
  const draftIdRef = useRef<number | null>(null);
  const [aiFilled, setAiFilled] = useState<WhatsAppAiFilledField[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const { audienceOptions, isListsLoading } = useAudienceListsAndCompany();
  const audience = useWhatsAppAudience(values.sendTo, true);
  const { media, attach, discard } = useWhatsAppMediaUpload(values.mediaType);

  const errors = useMemo(
    () => validateTemplateForm(values, Boolean(media.attachment)),
    [media.attachment, values]
  );

  const update = useCallback(
    <K extends keyof WhatsAppTemplateFormValues>(key: K, value: WhatsAppTemplateFormValues[K]) => {
      setValues(current => ({ ...current, [key]: value }));
      setAiFilled(current => current.filter(field => field !== key));
    },
    []
  );

  const changeMediaType = useCallback(
    (mediaType: WhatsAppTemplateFormValues["mediaType"]) => {
      discard();
      update("mediaType", mediaType);
    },
    [discard, update]
  );

  const updateSample = useCallback(
    (index: number, column: string) =>
      setValues(current => ({ ...current, samples: { ...current.samples, [index]: column } })),
    []
  );

  const addButton = useCallback(
    (type: WhatsAppButtonType) =>
      setValues(current => ({ ...current, buttons: [...current.buttons, createButtonDraft(type)] })),
    []
  );

  const updateButton = useCallback(
    (id: string, patch: Partial<WhatsAppButtonDraft>) =>
      setValues(current => ({
        ...current,
        buttons: current.buttons.map(button => (button.id === id ? { ...button, ...patch } : button)),
      })),
    []
  );

  const changeButtonType = useCallback(
    (id: string, type: WhatsAppButtonType) =>
      setValues(current => ({
        ...current,
        buttons: current.buttons.map(button =>
          button.id === id ? { ...createButtonDraft(type), id } : button
        ),
      })),
    []
  );

  const removeButton = useCallback(
    (id: string) =>
      setValues(current => ({
        ...current,
        buttons: current.buttons.filter(button => button.id !== id),
      })),
    []
  );

  const reportFailure = useCallback(
    (error: unknown) => {
      const detail = getThrownApiDetail(error);
      if (detail) {
        showApiDetailToast(detail);
      } else {
        message.error(t(`${FORM_KEY}.errors.saveFailed`));
      }
    },
    [t]
  );

  const submit = useCallback(async () => {
    setShowErrors(true);
    if (hasFormErrors(errors) || media.isUploading) {
      return;
    }
    setIsSaving(true);
    try {
      const response = await WhatsAppCampaignAPI.submitTemplate(
        toCreatePayload(values, media.attachment)
      );
      if (response.success) {
        setSubmittedName(values.name.trim());
      } else {
        message.error(
          [response.message, ...(response.errors ?? [])].filter(Boolean).join(" ") ||
            t(`${FORM_KEY}.errors.saveFailed`)
        );
      }
    } catch (error) {
      reportFailure(error);
    } finally {
      setIsSaving(false);
    }
  }, [errors, media.attachment, media.isUploading, reportFailure, t, values]);

  const saveDraft = useCallback(async () => {
    if (errors.name) {
      setShowErrors(true);
      return;
    }
    const data = toDraftData(values, media.attachment);
    setIsSaving(true);
    try {
      const draft = draftIdRef.current
        ? await CampaignDraftAPI.save(draftIdRef.current, { data })
        : await CampaignDraftAPI.create("whatsapp", data);
      draftIdRef.current = draft.draft_id;
      message.success(t("campaignFlow.common.saveDraftSuccess"));
      onDraftSaved();
    } catch (error) {
      reportFailure(error);
    } finally {
      setIsSaving(false);
    }
  }, [errors.name, media.attachment, onDraftSaved, reportFailure, t, values]);

  const generateFromPrompt = useCallback(
    async (prompt: string) => {
      setIsGenerating(true);
      try {
        const response = await WhatsAppCampaignAPI.generateTemplate(prompt.trim());
        const { values: nextValues, filled } = applyGeneratedFields(
          values,
          response.success ? (response.fields ?? {}) : {},
          audienceOptions.map(option => option.name)
        );
        if (!response.success || !filled.length) {
          message.warning(
            [response.message, ...(response.errors ?? [])].filter(Boolean).join(" ") ||
              t(`${FORM_KEY}.ai.noFields`)
          );
          return;
        }
        if (filled.includes("mediaType")) {
          discard();
        }
        setValues(nextValues);
        setAiFilled(filled);
      } catch (error) {
        const detail = getThrownApiDetail(error);
        if (detail) {
          showApiDetailToast(detail);
        } else {
          message.error(t(`${FORM_KEY}.ai.failed`));
        }
      } finally {
        setIsGenerating(false);
      }
    },
    [audienceOptions, discard, t, values]
  );

  const closeSubmitted = useCallback(() => setSubmittedName(null), []);

  return {
    values,
    errors,
    showErrors,
    isSaving,
    submittedName,
    audienceOptions,
    isListsLoading,
    audience,
    media,
    isReadOnly: initialTemplate?.status === "pending",
    isAiMode: useAi && !initialTemplate,
    aiFilled,
    isGenerating,
    generateFromPrompt,
    update,
    changeMediaType,
    updateSample,
    addButton,
    updateButton,
    changeButtonType,
    removeButton,
    attachMedia: attach,
    removeMedia: discard,
    submit,
    saveDraft,
    closeSubmitted,
  };
};

export type WhatsAppTemplateFormState = ReturnType<typeof useWhatsAppTemplateForm>;
